<template>
  <dx-modal :value="show" title="Utilizar saldo em conta" size="md" @input="close()"
            id="modal-cash-register-use-credit">
    <div class="loading loading-lg" v-if="loading"></div>
    <div class="columns" v-else>
      <div class="column col-12 form-group" :class="{'has-error': $v.form.holder.id.$error}">
        <label class="form-label">Titular do saldo</label>
        <dx-autocomplete
          id="credit-holder"
          v-model="holder"
          :readonly="!!form.holder.id"
          :source="findPatient"
          label="name"
          track-by="id"
          @select="setHolder"
          :debounce="800"
          @blur="$v.form.holder.id.$touch()"
          placeholder="Nome, data de nascimento ou CPF do titular"
        >
          <button slot="action" @click="unsetHolder"
                  class="btn btn-action input-group-btn btn-icon"
                  :class="holder ? 'btn-gray' : 'btn-neutral'"
                  tabindex="-1">
            <fa-icon :icon="['fal', holder ? 'times' : 'search']"></fa-icon>
          </button>
          <template v-slot="{ item }">
            <div>{{ item.name }}</div>
            <small class="text-primary" v-if="item.id === null">Paciente não encontrado</small>
          </template>
        </dx-autocomplete>
        <div class="form-input-hint" v-if="otherHolder">
          O saldo de <strong>{{ form.holder.name }}</strong> será usado no atendimento de
          <strong>{{ patient.name }}</strong>.
        </div>
        <template v-if="$v.form.holder.id.$error">
          <div class="form-input-hint">Campo obrigatório</div>
        </template>
      </div>
      <div class="column col-4 col-sm-12 form-group">
        <label class="form-label">Saldo disponível</label>
        <input type="text"
               class="form-input text-right text-bold"
               readonly
               :value="balance | currency">
      </div>
      <div class="column col-4 col-sm-12 form-group">
        <label class="form-label">Restante a pagar</label>
        <input type="text" class="form-input text-right" readonly :value="maxValue | currency">
      </div>
      <div class="column col-4 col-sm-12 form-group" :class="{'has-error': $v.form.value.$error}">
        <label class="form-label">Valor a utilizar</label>
        <dx-input-number id="credit-use-value" v-model="form.value"
                         class="form-input text-right text-bold"
                         :precision="2" maxlength="10" @blur="$v.form.value.$touch()"/>
        <template v-if="$v.form.value.$error">
          <div class="form-input-hint" v-if="!$v.form.value.minValue">Informe um valor</div>
          <div class="form-input-hint" v-else-if="!$v.form.value.maxValue">
            Maior que o saldo disponível ou que o restante a pagar
          </div>
        </template>
      </div>
    </div>
    <template slot="footer">
      <button class="btn btn-primary btn-icon btn-icon-left mr-1"
              :disabled="loading || loadingBalance" @click="confirm">
        <fa-icon :icon="['fal', 'check']"/>Utilizar
      </button>
      <button class="btn" @click="close()">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import { round } from 'lodash';
import { required, minValue, maxValue } from 'vuelidate/lib/validators';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    patient: {
      type: Object,
      default: null,
    },
    maxValue: {
      type: Number,
      default: 0,
    },
    alreadyUsed: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      loadingBalance: false,
      holder: null,
      balance: 0,
      form: {
        holder: {
          id: '',
          name: '',
        },
        value: 0,
      },
    };
  },
  computed: {
    limit() {
      return round(Math.max(Math.min(this.balance, this.maxValue), 0), 2);
    },
    otherHolder() {
      return !!this.patient && !!this.form.holder.id && this.form.holder.id !== this.patient.id;
    },
  },
  validations() {
    return {
      form: {
        holder: {
          id: { required },
        },
        value: {
          required,
          minValue: minValue(0.01),
          maxValue: maxValue(this.limit),
        },
      },
    };
  },
  async mounted() {
    this.loading = true;
    if (this.patient && this.patient.id) {
      await this.setHolder(this.patient);
    }
    this.loading = false;
  },
  methods: {
    findPatient(search) {
      return this.$http.get('/patients/search', { params: { search } })
        .then(({ data }) => {
          if (data.items.length === 0) {
            return [{ id: null, name: search }];
          }
          return data.items;
        })
        .catch(() => []);
    },
    async setHolder(data) {
      if (!data || !data.id) {
        this.unsetHolder();
        return;
      }
      this.holder = { id: data.id, name: data.name };
      this.form.holder = { id: data.id, name: data.name };
      await this.loadBalance();
      this.form.value = this.limit;
    },
    unsetHolder() {
      this.holder = null;
      this.form.holder = { id: '', name: '' };
      this.balance = 0;
      this.form.value = 0;
    },
    loadBalance() {
      this.loadingBalance = true;
      const params = { patientId: this.form.holder.id };
      return this.$http.get('/patient-credits/balance', { params })
        .then(({ data }) => {
          const used = this.alreadyUsed[this.form.holder.id] || 0;
          this.balance = round(Math.max((data.balance || 0) - used, 0), 2);
        })
        .catch(() => {
          this.balance = 0;
        })
        .finally(() => {
          this.loadingBalance = false;
        });
    },
    confirm() {
      this.$v.form.$touch();
      if (this.$v.form.$error) return;
      this.$emit('confirm', {
        holderId: this.form.holder.id,
        holderName: this.form.holder.name,
        value: round(this.form.value, 2),
      });
      this.close();
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>
